import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconLogout, IconUser } from '@tabler/icons-react';
import TeeAvatar from '@/components/ui/tee-avatar';
import { useAuth } from '@/hooks/useAuth';
import { useTeeLocations } from '@/hooks/useTeeLocations';

// Humanized role labels for the user-menu badge.
const ROLE_LABELS: Record<string, string> = {
  'box-pro': 'Box Pro',
  'box-pro-feature': 'Box Pro',
  'fitness-pro': 'Fitness Pro',
  'location-admin': 'Location Admin',
  'location-admin-feature': 'Location Admin',
  admin: 'Admin',
  'tbx-admin': 'Tee Box Admin',
};

/**
 * The header user avatar + dropdown. Shared by both the launcher header
 * (TopBar) and the module header (ModuleTopBar) so they never drift.
 * Gold avatar in the bar; forest avatar + identity + Sign out in the menu.
 */
export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { locations, getAllLocations } = useTeeLocations({ initialPageSize: 500 });
  const locationsFetchedRef = useRef(false);
  const [open, setOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        avatarRef.current &&
        !avatarRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  const handleToggle = () => {
    if (!locationsFetchedRef.current && (user.locationIds?.length ?? 0) > 0) {
      locationsFetchedRef.current = true;
      getAllLocations();
    }
    if (avatarRef.current) {
      const r = avatarRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 8, left: r.right - 280 });
    }
    setOpen((p) => !p);
  };

  const handleLogout = () => {
    setOpen(false);
    logout();
  };

  const initials = (user.firstName?.charAt(0) ?? '') + (user.lastName?.charAt(0) ?? '');
  const roleLabel = user.roleName ? ROLE_LABELS[user.roleName] ?? user.roleName.replace(/-/g, ' ') : undefined;
  const locationName = user.locationIds?.length
    ? locations.find((l) => l.id === user.locationIds![0])?.name
    : undefined;

  return (
    <>
      <div
        ref={avatarRef}
        onClick={handleToggle}
        className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <TeeAvatar
          size="32px"
          style={user.profilePhoto ? 'Image' : 'Text'}
          type="Gold"
          initials={initials}
          src={user.profilePhoto}
        />
      </div>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-[70] w-[280px] rounded-[10px] bg-[var(--background-surface-floating)] border border-[var(--border-default)] overflow-hidden"
            style={{ top: `${pos.top}px`, left: `${pos.left}px`, boxShadow: 'var(--section-shadow)' }}
          >
            {/* Identity */}
            <div className="flex flex-col gap-3 p-4">
              <div className="flex gap-3 items-center">
                <TeeAvatar
                  size="44px"
                  style={user.profilePhoto ? 'Image' : 'Text'}
                  type="Forest"
                  initials={initials}
                  src={user.profilePhoto}
                />
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <p className="font-semibold text-body-s text-[var(--text-content-title)] truncate">
                    {user.firstName} {user.lastName}
                  </p>
                  {user.tag && (
                    <p className="text-body-xs font-medium text-[var(--text-content-title)] truncate">{user.tag}</p>
                  )}
                  <p className="text-body-xs text-[var(--text-content-sub-title)] truncate">{user.email}</p>
                </div>
              </div>
              {(roleLabel || locationName) && (
                <div className="flex items-center gap-2 flex-wrap">
                  {roleLabel && (
                    <span className="text-[11px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--color-gold-subtle)] text-[var(--color-text-gold)] border border-[var(--color-gold-border)]">
                      {roleLabel}
                    </span>
                  )}
                  {locationName && (
                    <span className="text-body-xs text-[var(--text-content-sub-title)] truncate">{locationName}</span>
                  )}
                </div>
              )}
            </div>

            <div className="h-px bg-[var(--border-default)]" />

            {/* Profile */}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/profile');
              }}
              className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-body-s font-medium text-[var(--text-content-title)] hover:bg-[var(--background-card-elevated)] transition-colors focus:outline-none"
            >
              <IconUser size={16} />
              {t('navigation.myProfile')}
            </button>

            <div className="h-px bg-[var(--border-default)]" />

            {/* Sign out */}
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-body-s font-semibold text-[var(--color-maroon)] hover:bg-[var(--background-card-elevated)] transition-colors focus:outline-none"
            >
              <IconLogout size={16} />
              Sign out
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

export default UserMenu;
